/**
 * V_CRT_PDP_03 | Buy 2 Get 2 as third tier - BFS
 *
 * Swaps the Kaching bundle widget to the arm's duplicate product, so the shopper is offered — and charged for — the
 * offer ladder that belongs to their variation. Control is never touched.
 *
 * Every product id, class name and timing comes from the section settings via the c-bundle-swap-pdp03 snippet.
 * Nothing about the test is written here.
 *
 * WHAT IS SWAPPED, AND WHY IT IS A REPLACEMENT RATHER THAN AN ATTRIBUTE CHANGE
 * Confirmed against Kaching's own module on this shop:
 *   - <kaching-bundle product-id="…"> is the placeholder Kaching renders and then initializes.
 *   - window.__kachingBundlesInitializeInternal is the function Kaching's own auto-init calls, so calling it
 *     ourselves is a deterministic trigger rather than relying on its passive placeholder MutationObserver.
 * Re-pointing product-id alone does not re-initialize the widget, but re-pointing it AND clearing data-initialized
 * does, and Kaching then replaces its own children in place. The shopper never sees a gap and never sees a blank.
 *
 * This theme renders the widget more than once per product page — the product info column and the sticky quick-buy
 * bar. All of them are swapped, and the hold is only released once every one has settled: revealing after the first
 * would show a sticky bar still offering the control bundle.
 *
 * CONTROL IS UNTOUCHED, INCLUDING ITS TIMING
 * Without an assigned variant nothing is swapped and nothing is hidden: a control shopper's buy box renders exactly
 * as it does with the test switched off. The test must cost control nothing at all, delay included.
 */
(function () {
  'use strict';

  var config = document.getElementById('c-bundle-swap-pdp03-config');

  if (!config) return;

  var data = config.dataset;
  var READY_ATTR = 'data-c-bsp03-ready';

  /* Put on <body> the moment we have an arm to run, for styling that has to follow the test. Deliberately not the
     Intelligems variant class: that one is absent while an arm is previewed through the Rollout setting, so anything
     keyed on it would look right in the test and wrong in preview. */
  var ACTIVE_CLASS = 'c-bsp03-on';

  var varAClass = data.varAClass || '';
  var varBClass = data.varBClass || '';
  var variantWait = toInt(data.variantWait, 0);
  var kachingWait = toInt(data.kachingWait, 0);
  var swapTimeout = toInt(data.swapTimeout, 0);
  var maxAttempts = toInt(data.maxAttempts, 1);

  var pairs = parsePairs(data.pairs);
  var byBaseId = {};
  var started = false;

  for (var index = 0; index < pairs.length; index++) {
    byBaseId[String(pairs[index].base)] = pairs[index];
  }

  /* Nothing configured to swap: never leave the widget held. */
  if (!pairs.length) {
    revealAll();
    return;
  }

  function toInt(value, fallback) {
    var parsed = parseInt(value, 10);

    return isNaN(parsed) ? fallback : parsed;
  }

  function parsePairs(raw) {
    try {
      var parsed = JSON.parse(raw || '[]');

      return Object.prototype.toString.call(parsed) === '[object Array]' ? parsed : [];
    } catch (error) {
      /* A malformed payload must not strand the widget. */
      return [];
    }
  }

  /* ---------------------------------------------------------------------------------------------------------------
     Revealing
     --------------------------------------------------------------------------------------------------------------- */

  /**
   * Stamps a widget as settled, which is what the stylesheet watches. Reveal is per element rather than global so a
   * widget that fails to swap can still be shown without claiming the others are done.
   */
  function reveal(element) {
    if (element && !element.hasAttribute(READY_ATTR)) element.setAttribute(READY_ATTR, '');
  }

  function revealAll() {
    var widgets = document.querySelectorAll('kaching-bundle');

    for (var index = 0; index < widgets.length; index++) {
      reveal(widgets[index]);
    }
  }

  /* ---------------------------------------------------------------------------------------------------------------
     Variant resolution
     --------------------------------------------------------------------------------------------------------------- */

  /**
   * Answers both which arm to run and whether that is settled yet, which are different questions: an unassigned
   * visitor and a visitor whose assignment has not arrived yet both present as "no variant class", and only the first
   * should be left on control for good.
   *
   * The rollout setting is what makes a winning variant easy to ship and is how an arm is previewed before the
   * experiment starts, which is why it is read before the variant class.
   */
  function decideArm() {
    if (data.rollout === 'all_var_a') return { settled: true, arm: 'varA' };
    if (data.rollout === 'all_var_b') return { settled: true, arm: 'varB' };

    var classes = document.body.classList;

    if (varAClass && classes.contains(varAClass)) return { settled: true, arm: 'varA' };
    if (varBClass && classes.contains(varBClass)) return { settled: true, arm: 'varB' };

    return { settled: false, arm: null };
  }

  /* ---------------------------------------------------------------------------------------------------------------
     The swap
     --------------------------------------------------------------------------------------------------------------- */

  /**
   * Waits for the Kaching app, for as long as it actually takes.
   *
   * KACHING LOADS LAZILY, AND THAT IS WHAT USED TO LOSE THE TEST. On this shop
   * __kachingBundlesInitializeInternal does not exist until the shopper scrolls, moves the mouse or taps — or until
   * roughly ten seconds have passed on their own. A shopper who lands and reads for a moment without moving is
   * completely normal, and the earlier build gave up on them after a fixed deadline (set to 984ms live): they kept
   * the control ladder while Intelligems still counted them in the arm. That is the worst thing a test can do —
   * it does not just lose the shopper, it quietly poisons the result for every other shopper in that arm.
   *
   * So the deadline is now a long backstop rather than a verdict, and two things drive the check: a slow poll, and
   * the very interaction events that wake Kaching up, so the swap follows as closely behind it as possible. Nothing
   * is hidden while we wait, so waiting costs the shopper nothing — they simply see the control offer until the
   * arm's offer replaces it.
   *
   * Both conditions have to hold: the initializer has to exist AND a widget has to be on the page to re-point.
   */
  function whenKachingReady(onReady, msLeft) {
    var INTERACTIONS = ['pointerdown', 'pointermove', 'touchstart', 'scroll', 'keydown', 'wheel'];
    /* Checked often rather than politely: the whole cost of a miss is the shopper watching the control offer for
       another tick, and the check is a typeof plus one querySelector. */
    var POLL_MS = 100;
    var waited = 0;
    var finished = false;
    var timer;

    function ready() {
      return (
        typeof window.__kachingBundlesInitializeInternal === 'function' &&
        document.querySelector('kaching-bundle') !== null
      );
    }

    function stop() {
      window.clearInterval(timer);

      for (var index = 0; index < INTERACTIONS.length; index++) {
        window.removeEventListener(INTERACTIONS[index], check, true);
      }
    }

    function check() {
      if (finished) return;

      if (ready()) {
        finished = true;
        stop();
        onReady();
        return;
      }

      waited += POLL_MS;

      if (msLeft > 0 && waited >= msLeft) {
        /* Kaching never arrived at all. Nothing of ours can render; the page is already correct as it stands. */
        finished = true;
        stop();
      }
    }

    for (var index = 0; index < INTERACTIONS.length; index++) {
      window.addEventListener(INTERACTIONS[index], check, true);
    }

    timer = window.setInterval(check, POLL_MS);
    check();
  }

  /**
   * Re-points the widget Kaching already owns at the arm's product and asks it to rebuild itself.
   *
   * TWO EARLIER VERSIONS OF THIS WERE WRONG, BOTH IN WAYS THE SHOPPER COULD SEE.
   *
   * The first built a second <kaching-bundle> beside the original, initialized that, and swapped it in only once it
   * had rendered, so there would be no gap. Against the live shop Kaching usually left that detached sibling empty:
   * the attempt timed out, the retries burned, and the shopper silently kept the control offer while Intelligems
   * counted them in the variant. Re-pointing the element Kaching already owns is what it actually responds to.
   *
   * The second emptied the element before re-initializing. That produced a buy box blank for several seconds, which
   * a stylesheet then had to hide — and because the stylesheet could not know the arm, it held CONTROL shoppers back
   * too. Not emptying it removes the blank entirely: Kaching replaces its own children, so the control offer stays
   * on screen until the new one takes its place, measured at ~1.4s with the widget never once blank. No hold is
   * needed, and control is left exactly as it would be with the test switched off.
   */
  function attemptSwap(element, productId, attempt, done) {
    var originalId = element.getAttribute('product-id');
    var settled = false;

    function build(id) {
      element.setAttribute('product-id', id);
      element.removeAttribute('data-initialized');
      window.__kachingBundlesInitializeInternal();
    }

    /* Kaching keeps the old children on screen until the new ones are ready, so "has children" cannot tell us the
       rebuild happened — it is true the whole time. Every offer tier carries a deal-bar id that belongs to its own
       product, so the list of those ids changing is the one reliable signal that the arm's offer actually rendered. */
    function barSignature() {
      return Array.prototype.map
        .call(element.querySelectorAll('[data-deal-bar-id]'), function (bar) {
          return bar.getAttribute('data-deal-bar-id');
        })
        .join('|');
    }

    var signatureBefore = barSignature();

    function rebuilt() {
      return barSignature() !== signatureBefore;
    }

    function confirmSwap() {
      if (settled) return;
      settled = true;
      observer.disconnect();
      window.clearTimeout(fallback);
      done(element);
    }

    function retryOrGiveUp() {
      if (settled) return;
      settled = true;
      observer.disconnect();
      window.clearTimeout(fallback);

      if (attempt < maxAttempts) {
        attemptSwap(element, productId, attempt + 1, done);
        return;
      }

      /* Out of retries. Put the control offer back so the shopper is offered the bundle they can actually see — the
         arm is simply lost for them, which the Intelligems data reflects. */
      console.warn('[V_CRT_PDP_03] Kaching bundle swap failed, restoring the control widget.');
      build(originalId);
      window.setTimeout(function () {
        done(element);
      }, 1500);
    }

    var fallback = window.setTimeout(function () {
      if (rebuilt()) confirmSwap();
      else retryOrGiveUp();
    }, swapTimeout);

    var observer = new MutationObserver(function () {
      if (settled || !rebuilt()) return;
      confirmSwap();
    });

    observer.observe(element, { childList: true, subtree: true });

    build(productId);
  }

  function swapBundles(arm) {
    var widgets = document.querySelectorAll('kaching-bundle');
    var swappable = Array.prototype.filter.call(widgets, function (widget) {
      var pair = byBaseId[widget.getAttribute('product-id')];

      return pair && pair[arm];
    });

    /* Anything we are not swapping is already correct, so it can be shown straight away. */
    Array.prototype.forEach.call(widgets, function (widget) {
      if (swappable.indexOf(widget) === -1) reveal(widget);
    });

    if (!swappable.length) return;

    var outstanding = swappable.length;
    var settledElements = [];

    function done(element) {
      settledElements.push(element);
      outstanding -= 1;

      if (outstanding > 0) return;

      for (var index = 0; index < settledElements.length; index++) {
        reveal(settledElements[index]);
      }
    }

    swappable.forEach(function (widget) {
      attemptSwap(widget, byBaseId[widget.getAttribute('product-id')][arm], 1, done);
    });
  }

  /* ---------------------------------------------------------------------------------------------------------------
     Gallery
     --------------------------------------------------------------------------------------------------------------- */

  /**
   * Keeps the product gallery in step with the colour picked inside the Kaching bundle.
   *
   * In control the bundle holds the same product as the page, so Kaching's own variant-image sync lines up. Once the
   * bundle holds a duplicate, its colour selection no longer refers to anything the page's gallery knows about and
   * the images stop following.
   *
   * This drives the theme's own colour swatch — find the matching radio, check it, dispatch change — and the theme
   * then runs its full native path: variant resolved, section re-rendered, filtered indexes recomputed so other
   * colourways are hidden, price and carousel updated. The end state is identical to a shopper clicking the swatch.
   *
   * EVERY LOOKUP IS REDONE ON EVERY CALL, DELIBERATELY. The theme's partial re-render replaces variant-picker, so
   * anything held across calls is detached afterwards and dispatching on it silently does nothing. Caching the picker
   * is exactly what would make this work once and then never again.
   */
  function syncGalleryWithBundle() {
    var pair = null;

    for (var index = 0; index < pairs.length; index++) {
      if (pairs[index].colorOptionName && pairs[index].colorOptionPosition) {
        pair = pairs[index];
        break;
      }
    }

    if (!pair) return;

    var colorOptionName = normalise(pair.colorOptionName);
    var radioSelector = 'input[type="radio"][name$="-option' + pair.colorOptionPosition + '"]';
    var syncing = false;

    function normalise(value) {
      return String(value == null ? '' : value).replace(/\s+/g, ' ').trim().toLowerCase();
    }

    function selectColour(colour) {
      var wanted = normalise(colour);

      if (!wanted) return;

      var picker = document.querySelector('variant-picker');

      if (!picker) return;

      var radios = picker.querySelectorAll(radioSelector);

      for (var index = 0; index < radios.length; index += 1) {
        var radio = radios[index];
        var label = radio.labels && radio.labels[0];

        if (normalise(label && label.textContent) !== wanted) continue;

        /* Already on this colourway. Returning here is also what stops a feedback loop if the re-render ends up
           writing back to the bundle. */
        if (radio.checked) return;

        syncing = true;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        window.requestAnimationFrame(function () {
          syncing = false;
        });

        return;
      }
    }

    /* Kaching renders colour as a div with data-name / data-value, not a select, so there is no change event to
       listen for and the attribute is observed instead. Watching the whole body also covers Kaching re-rendering the
       bundle when a different quantity tier is picked. */
    new MutationObserver(function (records) {
      if (syncing) return;

      for (var index = 0; index < records.length; index += 1) {
        var target = records[index].target;

        if (!target.dataset || !target.dataset.name) continue;
        if (normalise(target.dataset.name) !== colorOptionName) continue;
        if (!target.closest || !target.closest('kaching-bundle')) continue;

        selectColour(target.dataset.value);
      }
    }).observe(document.body, { attributes: true, subtree: true, attributeFilter: ['data-value'] });
  }

  /* ---------------------------------------------------------------------------------------------------------------
     Wiring
     --------------------------------------------------------------------------------------------------------------- */

  function begin() {
    if (started) return true;

    var decision = decideArm();

    if (!decision.settled) return false;

    started = true;

    if (!decision.arm) {
      /* Control, excluded, or Intelligems could not place this shopper — the page is already correct. */
      revealAll();
      return true;
    }

    document.body.classList.add(ACTIVE_CLASS);

    whenKachingReady(function () {
      swapBundles(decision.arm);
      /* Only wired up for a swapped bundle. In control Kaching's own sync already works and this would double-fire. */
      syncGalleryWithBundle();
    }, kachingWait);

    return true;
  }

  /**
   * Why the variant class is watched for rather than waited on with an event.
   *
   * c-intelligems-tests.js adds the class only once BOTH DOMContentLoaded and ig:ready have happened. This script is
   * deferred, so it may execute after Intelligems has already resolved: at that moment the class may not be there
   * yet, and ig:ready may have already fired, so listening for it would wait for an event that never comes again.
   * Watching body's class list covers both orderings.
   *
   * The watch is given up after "Variant wait" so an unassigned visitor is not left with an observer running for the
   * life of the page — and the widget is revealed at that point rather than left held.
   */
  function start() {
    if (begin()) return;

    var timer = 0;

    var observer = new MutationObserver(function () {
      if (!begin()) return;

      observer.disconnect();
      if (timer) window.clearTimeout(timer);
    });

    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    if (variantWait > 0) {
      timer = window.setTimeout(function () {
        observer.disconnect();
        if (!started) revealAll();
      }, variantWait);
    }
  }

  /* document.body has to exist for the watch above, which a deferred script guarantees. */
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
